import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Search } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const ReportMensile = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/monthly-report`, {
        params: { year, month }
      });
      setReport(response.data);
      toast.success('Report generato con successo');
    } catch (error) {
      toast.error('Errore nel caricamento del report');
    } finally {
      setLoading(false);
    }
  };

  const monthNames = [
    'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-heading text-2xl">Report Pagamenti Mensile</CardTitle>
        <p className="text-sm text-muted-foreground">
          Visualizza il riepilogo dei pagamenti raggruppati per codice fiscale
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Selettore Mese/Anno */}
        <div className="flex items-end gap-4">
          <div className="flex-1 space-y-2">
            <Label htmlFor="year">Anno</Label>
            <Input
              id="year"
              type="number"
              min="2020"
              max="2099"
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              data-testid="report-year-input"
            />
          </div>
          <div className="flex-1 space-y-2">
            <Label htmlFor="month">Mese</Label>
            <select
              id="month"
              value={month}
              onChange={(e) => setMonth(parseInt(e.target.value))}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="report-month-select"
            >
              {monthNames.map((name, idx) => (
                <option key={idx + 1} value={idx + 1}>{name}</option>
              ))}
            </select>
          </div>
          <Button
            onClick={fetchReport}
            disabled={loading}
            className="btn-scale"
            data-testid="generate-report-button"
          >
            <Search className="w-4 h-4 mr-2" />
            {loading ? 'Caricamento...' : 'Genera Report'}
          </Button>
        </div>

        {/* Report Table */}
        {report && (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-primary/10 rounded-lg">
              <div>
                <h3 className="font-semibold text-lg">
                  Report {monthNames[report.month - 1]} {report.year}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {report.total_bookings} prenotazioni totali
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Totale Generale</p>
                <p className="font-heading font-bold text-3xl text-primary">
                  € {report.grand_total.toFixed(2)}
                </p>
              </div>
            </div>

            {report.users.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                Nessuna prenotazione in questo periodo
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Codice Fiscale</TableHead>
                      <TableHead>Nome</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Comune</TableHead>
                      <TableHead className="text-center">N. Prenotazioni</TableHead>
                      <TableHead className="text-right">Totale</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.users.map((user) => (
                      <TableRow key={user.codice_fiscale} data-testid={`report-row-${user.codice_fiscale}`}>
                        <TableCell className="font-mono text-sm">{user.codice_fiscale}</TableCell>
                        <TableCell>{user.nome_cognome}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{user.email}</TableCell>
                        <TableCell className="text-sm">{user.comune_residenza || 'N/A'}</TableCell>
                        <TableCell className="text-center">{user.booking_count}</TableCell>
                        <TableCell className="text-right font-semibold">
                          € {user.total_amount.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-muted font-semibold">
                      <TableCell colSpan={4}>TOTALE</TableCell>
                      <TableCell className="text-center">{report.total_bookings}</TableCell>
                      <TableCell className="text-right">€ {report.grand_total.toFixed(2)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}

        {!report && (
          <div className="text-center py-12 text-muted-foreground">
            Seleziona mese e anno e clicca "Genera Report" per visualizzare i dati
          </div>
        )}
      </CardContent>
    </Card>
  );
};
